/**
 * tk-fsb — oslava odemčení dopravy zdarma (2026-08-29).
 *
 * Lišta (`snippets/tk-free-shipping-bar.liquid`) je jinak čistý Liquid+CSS;
 * tenhle modul jen pozná PŘECHOD „ještě zbývá X" → „máte dopravu zdarma"
 * a přehraje sdílenou oslavu `.tk-perk--celebrate` (CSS v snippetu lišty).
 * Stejná mechanika jako oslava dárku v `assets/tk-gift.js`:
 *
 * ⚠️ Přechod NEJDE poznat z DOMu — lišta se po každé změně košíku
 * přerenderuje a zmorfuje. Předchozí stav drží sessionStorage (přežije
 * i navigaci z PDP).
 * ⚠️ Instancí `<tk-fsb>` je na stránce víc (lišta na /cart + lišta draweru
 * v hlavičce) — první, která přechod uvidí, přepíše uložený stav; ostatním
 * dovolí oslavu krátké společné okno `celebrateUntil`.
 * ⚠️ Morph přepisuje `class` podle serverového HTML → třídu oslavy hlídá
 * MutationObserver a vrací ji v mikrotasku (před přepočtem stylů), takže
 * běžící animace pokračují bez restartu.
 * ⚠️ Po morphu neběží `connectedCallback` (element se patchuje na místě) —
 * proto modulový listener na `cart:update` s odloženými průchody.
 */

const DONE_KEY = 'tk-fsb-done';
/** Musí pokrýt nejdelší animaci oslavy (dech 2,8 s) + rezervu. */
const CELEBRATE_MS = 3200;
const PASSES = [260, 1400];

let lastCelebrationAt = 0;
let celebrateUntil = 0;

/**
 * @param {boolean} done
 * @returns {boolean} má se přehrát oslava
 */
function shouldCelebrate(done) {
  let prev = null;
  try {
    prev = sessionStorage.getItem(DONE_KEY);
  } catch {
    /* private mode — oslava se prostě nepřehraje */
  }

  const now = done ? '1' : '0';
  if (prev !== now) {
    try {
      sessionStorage.setItem(DONE_KEY, now);
    } catch {
      /* nevadí */
    }
  }

  // `prev === null` = první stránka návštěvy — splněný práh z minula není
  // důvod k oslavě, jen se stav zapamatuje.
  if (done && prev === '0') {
    celebrateUntil = Date.now() + 1200;
    return true;
  }

  return done && Date.now() < celebrateUntil;
}

class TkFsb extends HTMLElement {
  connectedCallback() {
    requestAnimationFrame(() => this.#maybeCelebrate());
  }

  /** Veřejný vstup pro modulový listener — po morphu connectedCallback neběží. */
  check() {
    this.#maybeCelebrate();
  }

  #maybeCelebrate() {
    if (!shouldCelebrate(this.dataset.done === '1')) return;
    this.#playWhenVisible();
  }

  /** Lišta v draweru je v DOMu i se zavřeným drawerem — čekej, až je vidět. */
  #playWhenVisible(attempt = 0) {
    const visible = this.isConnected && this.getClientRects().length > 0;

    if (!visible) {
      if (attempt > 20) return;
      window.setTimeout(() => this.#playWhenVisible(attempt + 1), 200);
      return;
    }

    this.#play();
  }

  #play() {
    // Zámek v modulu (data- atribut by morph vymazal, viz tk-gift.js).
    if (Date.now() - lastCelebrationAt < CELEBRATE_MS) return;
    lastCelebrationAt = Date.now();

    this.classList.add('tk-perk--celebrate');

    // Třída musí přežít morph — vrací se v mikrotasku, před přepočtem stylů.
    const observer = new MutationObserver(() => {
      if (this.dataset.done === '1' && !this.classList.contains('tk-perk--celebrate')) {
        this.classList.add('tk-perk--celebrate');
      }
    });
    observer.observe(this, { attributes: true, attributeFilter: ['class'] });

    window.setTimeout(() => {
      observer.disconnect();
      this.classList.remove('tk-perk--celebrate');
    }, CELEBRATE_MS);
  }
}

if (!customElements.get('tk-fsb')) {
  customElements.define('tk-fsb', TkFsb);
}

document.addEventListener('cart:update', () => {
  PASSES.forEach((delay) => {
    window.setTimeout(() => {
      document.querySelectorAll('tk-fsb').forEach((el) => {
        if (el instanceof HTMLElement && typeof el.check === 'function') el.check();
      });
    }, delay);
  });
});
